import React, { useCallback, useRef, useState } from 'react';
import { useAppContext } from '@prisjakt/ui-app-context';
import AlternativePriceRow from '../alternative-price-row';
import ExpandButton from '../../components/expand-button';
import ExpandedInfo from '../../components/expanded-info';
import ReportButton from '../../components/report-button';
import { trackExpandPriceRowClick } from '../../tracking';
import { fetchSotyAwardsByStoreId } from '../../requests';
import * as S from '../styles';
import { Award } from '../../../../../../../types';
import { FeaturedColumns } from './featured-columns';
import { UnfeaturedColumns } from './unfeatured-columns';
import {
  IPriceRowProps,
  ReportRowType,
  ProductPageProductClick,
} from '../../types';
import { getTableLinkProps, logRevenueEvent } from '../../utils';
import { getStoreRating } from '../../../utils';

const PriceRow: React.FC<IPriceRowProps> = props => {
  const {
    alternativePrices,
    brand,
    brandRecommendedRetailer,
    campaign,
    priceDisplay,
    deliveryOptions = [],
    externalUri,
    isInternationalPrice,
    media,
    name,
    onClickOutBounce,
    onPriceClick,
    price,
    priceId,
    priceType,
    productId,
    productName,
    stock,
    store,
    storeInfo,
    isMemberPrice,
    shipping,
    deliveryTimeExperimentVal,
  } = props;
  const [showDisabledInfo, setShowDisabledInfo] = useState<boolean>(false);
  const [expanded, setExpanded] = useState<boolean>(false);
  const [priceRowReport, setPriceRowReport] = useState(ReportRowType.NONE);
  const [sotyBadge, setSotyBadge] = useState<Award | undefined | null>();
  const [reportRowId, setReportRowId] = useState<string | null>(null);
  const priceRowRef = useRef<HTMLDivElement>(null);
  const { t } = useAppContext();
  const featured = Boolean(store?.featured);
  const rating = getStoreRating(store);

  const startBounceTimer = useCallback(
    (id: string) => {
      // Start a timer when a user clicks out on a product
      // if they return to Prisjakt within 15 seconds
      // display an error report button in the price row.

      const startTime = Date.now();

      const onRefocus = () => {
        if (
          Date.now() - startTime > 15000 &&
          document.visibilityState === 'visible'
        ) {
          document.removeEventListener('visibilitychange', onRefocus);
          return;
        }

        if (document.visibilityState === 'visible') {
          setReportRowId(id);

          // Action event for tracking
          onClickOutBounce(productId, priceId);

          document.removeEventListener('visibilitychange', onRefocus);
        }
      };

      document.addEventListener('visibilitychange', onRefocus);
    },
    [onClickOutBounce, priceId, productId, setReportRowId]
  );

  const handleClickOut = useCallback(
    (e, id, isAlternative = false) => {
      setPriceRowReport(
        isAlternative ? ReportRowType.ALTERNATIVE : ReportRowType.DEFAULT
      );
      if (featured) {
        logRevenueEvent(id, productId);
      }

      if (externalUri) {
        if (IS_BROWSER) {
          startBounceTimer(id);
        }

        if (!showDisabledInfo && !e.defaultPrevented) {
          onPriceClick(
            featured,
            undefined,
            ProductPageProductClick,
            isAlternative,
            false
          );
        }
      } else {
        e.preventDefault();
        setShowDisabledInfo(!showDisabledInfo);
      }
    },
    [
      externalUri,
      onPriceClick,
      showDisabledInfo,
      startBounceTimer,
      setShowDisabledInfo,
      productId,
      featured,
    ]
  );
  const tableLinkProps = getTableLinkProps(
    featured,
    (e: React.MouseEvent) => handleClickOut(e, priceId),
    externalUri
  );

  const handleExpandButtonClick = () => {
    setExpanded(!expanded);
    if (!expanded) {
      trackExpandPriceRowClick(productId, productName, store?.id, store?.name);

      if (sotyBadge === undefined && store?.id) {
        fetchSotyAwardsByStoreId(store.id).then(response => {
          if (response?.data?.store?.storeOfTheYearAwards?.length) {
            setSotyBadge(response.data.store.storeOfTheYearAwards[0]);
          } else {
            setSotyBadge(null);
          }
        });
      }
    }
  };

  const handleClickOutAltPrice = useCallback(
    (e, id) => {
      handleClickOut(e, id, true);
    },
    [handleClickOut]
  );

  const closeReportOverlay = useCallback(() => {
    setPriceRowReport(ReportRowType.NONE);
    setReportRowId(null);
  }, [setReportRowId]);

  return (
    <React.Fragment key={store?.id || 'price-row'}>
      <S.RowCard expanded={expanded} featured={featured}>
        <S.Row
          className="pj-ui-price-row" // Classname is used to track click out
          featured={featured}
          noExternalUri={!externalUri}
          showReport={reportRowId === priceId}
          data-test="PriceRow"
          ref={priceRowRef}
          expanded={expanded}
        >
          <S.LeftColumn featured={featured}>
            <ExpandButton
              expanded={expanded}
              onClick={handleExpandButtonClick}
              title={t('product', 'price.more-info')}
              featured={featured}
            />
          </S.LeftColumn>

          {featured && externalUri && (
            <FeaturedColumns
              tableLinkProps={tableLinkProps}
              alternativePrices={alternativePrices}
              campaign={campaign}
              priceDisplay={priceDisplay}
              name={name}
              price={price}
              priceType={priceType}
              stock={stock}
              store={store}
              rating={rating}
              isInternationalPrice={isInternationalPrice}
              deliveryOptions={deliveryOptions}
              brandRecommendedRetailer={brandRecommendedRetailer}
              isMemberPrice={isMemberPrice}
              shipping={shipping}
              deliveryTimeExperimentVal={deliveryTimeExperimentVal}
            />
          )}

          {(!featured || !externalUri) && (
            <UnfeaturedColumns
              tableLinkProps={tableLinkProps}
              alternativePrices={alternativePrices}
              campaign={campaign}
              priceDisplay={priceDisplay}
              name={name}
              price={price}
              priceId={priceId}
              priceType={priceType}
              stock={stock}
              store={store}
              rating={rating}
              priceRowRef={priceRowRef}
              showDisabledInfo={showDisabledInfo}
              setShowDisabledInfo={setShowDisabledInfo}
            />
          )}
        </S.Row>

        {expanded && store && (
          <S.InfoRow data-test="ColumnExpanded">
            <ExpandedInfo
              brand={brand}
              brandRecommendedRetailer={brandRecommendedRetailer}
              deliveryOptions={deliveryOptions}
              priceId={priceId}
              rating={rating}
              store={store}
              storeInfo={storeInfo}
              sotyBadge={sotyBadge}
            />
          </S.InfoRow>
        )}
        {expanded && alternativePrices && (
          <S.AlternativePricesArea featured={featured}>
            <S.AlternativePricesInner featured={featured}>
              {alternativePrices.map(altPrice => {
                if (!altPrice) return null;

                return (
                  <React.Fragment key={altPrice.id || 'alternative-price-row'}>
                    <AlternativePriceRow
                      externalUri={externalUri}
                      campaign={campaign}
                      onClickOut={handleClickOutAltPrice}
                      price={altPrice}
                      store={store}
                      productImage={media.first}
                      tableLinkProps={tableLinkProps}
                      priceDisplay={priceDisplay}
                      isInternationalPrice={isInternationalPrice}
                    />
                    {priceRowReport === ReportRowType.ALTERNATIVE &&
                      reportRowId === altPrice.id && (
                        <S.AltReportRow>
                          <S.Column>
                            <ReportButton
                              store={store}
                              priceId={altPrice.id}
                              priceValue={
                                altPrice.price && altPrice.price.exclShipping
                              }
                              stockStatus={
                                altPrice.stock && altPrice.stock.status
                              }
                              productImage={media.first}
                              onClose={closeReportOverlay}
                              productName={altPrice.name}
                            />
                          </S.Column>
                        </S.AltReportRow>
                      )}
                  </React.Fragment>
                );
              })}
            </S.AlternativePricesInner>
          </S.AlternativePricesArea>
        )}
      </S.RowCard>
      {priceRowReport === ReportRowType.DEFAULT && reportRowId === priceId && (
        <S.ReportRow>
          <S.Column>
            <ReportButton
              store={store}
              priceId={priceId}
              priceValue={price && price.exclShipping}
              stockStatus={stock && stock.status}
              productImage={media.first}
              onClose={closeReportOverlay}
              productName={productName}
            />
          </S.Column>
        </S.ReportRow>
      )}
    </React.Fragment>
  );
};

export default PriceRow;
